// React
import { memo, useCallback, useEffect, useState } from "react";

// UI Components
import { Loader } from "ui/components/Loader";
import { Button } from "ui/components/Button/Button";
import { Modal } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";

// Services & Helpers
import { useTranslate } from "helpers";
import { getVehicleInsuranceAPI } from "services/api/calculations/vehicleinsurance/api";

// Types
import { VehicleInsuranceDetailProps } from "./types";
import { VehicleInsuranceDetailFormValues } from "services/api/calculations/vehicleinsurance/types";
import { Errors } from "services/forms/validation/types";

// Constants
import { initialVehicleInsuranceDetailForm } from "services/api/calculations/vehicleinsurance/consts";
import { CALCULATION_VEHICLE_INSURANCE_EDIT_PAGE_TITLE } from "ui/components/PageTitle/consts";

// Local Components
import VehicleInsuranceEditCard from "../../../VehicleInsuranceEditCard/VehicleInsuranceEditCard";

const VehicleInsuranceDetailCard: React.FC<VehicleInsuranceDetailProps> = (
  props
) => {
  const { vehicleInsuranceId } = props;

  const { t } = useTranslate();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [formValues, setFormValues] =
    useState<VehicleInsuranceDetailFormValues>(
      initialVehicleInsuranceDetailForm
    );

  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  useEffect(() => {
    if (vehicleInsuranceId) {
      fetchVehicleInsurance(vehicleInsuranceId);
    }
  }, [vehicleInsuranceId]);

  const fetchVehicleInsurance = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialVehicleInsuranceDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getVehicleInsuranceAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);

    setErrors({});
  };

  const handleEditSuccess = useCallback(() => {
    if (vehicleInsuranceId) fetchVehicleInsurance(vehicleInsuranceId);
    setShowEditModal(false);
  }, []);

  if (!vehicleInsuranceId) return <></>;

  return (
    <>
      <div className="  flex justify-center ">
        <div className="rounded-sm bg-white border max-w-lg w-full">
          <div className="divide-y divide-slate-200">
            <div className="flex justify-between items-center">
              <p className="text-sm font-semibold text-slate-600 px-4 py-3 uppercase">
                {t("admin.calculation.vehicleinsurance.detail.title")}
              </p>
              <div className="flex items-center gap-2 px-3 my-3 ">
                <Button
                  size="small"
                  variant="primary"
                  children={t("admin.change")}
                  onClick={() => setShowEditModal(true)}
                  startIcon="/media/icons/plus.svg"
                />
              </div>
            </div>
            <div className="px-4 py-2 min-h-[56px]">
              <div className="text-xs font-medium uppercase text-slate-600">
                {t("admin.calculation.vehicleinsurance.detail.fromKw")}
              </div>
              <div className="text-sm text-slate-500 truncate">
                {formValues.fromKw} kw
              </div>
            </div>
            <div className="px-4 py-2 min-h-[56px]">
              <div className="text-xs font-medium uppercase text-slate-600">
                {t("admin.calculation.vehicleinsurance.detail.toKw")}
              </div>
              <div className="text-sm text-slate-500 truncate">
                {formValues.toKw} kw
              </div>
            </div>
            <div className="px-4 py-2 min-h-[56px]">
              <div className="text-xs font-medium uppercase text-slate-600">
                {t("admin.calculation.vehicleinsurance.detail.price")}
              </div>
              <div className="text-sm text-slate-500 truncate">
                {formValues.price} $
              </div>
            </div>
          </div>
        </div>
      </div>
      {showEditModal && (
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
          <ModalHeader
            title={CALCULATION_VEHICLE_INSURANCE_EDIT_PAGE_TITLE.text}
            onHide={() => setShowEditModal(false)}
          />
          <ModalBody>
            <VehicleInsuranceEditCard
              vehicleInsuranceId={vehicleInsuranceId}
              onSuccess={handleEditSuccess}
            />
          </ModalBody>
        </Modal>
      )}
      <Loader isLoading={isLoading ?? false}> </Loader>
    </>
  );
};
export default memo(VehicleInsuranceDetailCard);
